/**
 * Turns a flat list of transactions into the series the dashboard charts need.
 *
 * This arithmetic lives in the client because no server query produces it:
 * `topSpending` hardcodes month buckets in both implementations and bakes that
 * into its schema as `monthKey`, and `getCashFlow` returns a weekly running
 * balance seeded from the card-issuer balance, which a BYOC-only company does
 * not have. See the plan for the full reasoning.
 *
 * Everything here is pure and takes an explicit `now`, so the boundaries are
 * testable rather than dependent on when the suite happens to run.
 */

import dayjs, { type Dayjs } from "utils/dayjs";
import TransactionsType from "enums/TransactionsType";

/** The scalars `GET_TRANSACTION_AMOUNTS` selects. */
export interface SpendTransaction {
  id: string;
  transactionDate?: number | string | null;
  amount?: number | null;
  currency?: string | null;
  accountId?: string | null;
  type?: string | null;
}

export interface DailyBucket {
  /** `YYYY-MM-DD`, the bucket key and a stable React key. */
  date: string;
  total: number;
}

export interface WeeklyBucket {
  /** 1-based position in its four-week block, for the shared x-axis. */
  week: number;
  current: number;
  previous: number;
}

export interface PeriodComparison {
  current: number;
  previous: number;
  /**
   * Rounded percent change, or null when the prior period had no spend. A
   * change from zero has no meaningful percentage, and reporting one reads as
   * an infinite jump.
   */
  changePercent: number | null;
}

/**
 * Card spend is stored negative and a credit positive; charts want spend as a
 * positive magnitude, so the sign is flipped and a credit reduces spend. A
 * `REFUND` row reduces spend whatever sign it is stored with.
 *
 * No unit conversion here. Amounts are minor units in DynamoDB, but the
 * `Transaction.amount` field resolver runs them through
 * `currency(...).toDecimalUnit()` before they leave the server, so what arrives
 * on the client is already decimal. Dividing again here was the bug that
 * rendered $1,000 as $10. It also would not have been right for zero-decimal
 * currencies, where the minor unit is not a hundredth.
 */
function magnitude(transaction: SpendTransaction): number {
  const amount = transaction.amount ?? 0;
  return transaction.type === TransactionsType.REFUND
    ? -Math.abs(amount)
    : -amount;
}

/**
 * `transactionDate` arrives as epoch millis from Dynamo but as an ISO string
 * once it has been through a GraphQL `Float` and back, depending on the path.
 * Tolerating both avoids a whole class of silent empty charts.
 */
function toDayjs(value: number | string | null | undefined) {
  if (value === null || value === undefined || value === "") return null;
  const asNumber = typeof value === "number" ? value : Number(value);
  const parsed = Number.isFinite(asNumber) ? dayjs(asNumber) : dayjs(value);
  return parsed.isValid() ? parsed : null;
}

/** Transactions falling inside [start, end), newest-first order irrelevant. */
function within(
  transactions: SpendTransaction[],
  start: Dayjs,
  end: Dayjs,
): SpendTransaction[] {
  return transactions.filter((transaction) => {
    const at = toDayjs(transaction.transactionDate);
    if (!at) return false;
    return at.isSameOrAfter(start) && at.isBefore(end);
  });
}

function sum(transactions: SpendTransaction[]): number {
  return transactions.reduce((total, t) => total + magnitude(t), 0);
}

/**
 * One bucket per day across the trailing `days`, oldest first.
 *
 * Days with no spend are present with a total of 0 rather than absent: a line
 * chart that skips empty days silently redraws the x-axis as uneven, which
 * misreports the shape of the trend.
 */
export function toDailyBuckets(
  transactions: SpendTransaction[],
  days: number,
  now: Dayjs = dayjs(),
): DailyBucket[] {
  const end = now.clone().startOf("day").add(1, "day");
  const start = end.clone().subtract(days, "days");

  const totals = new Map<string, number>();
  for (let i = 0; i < days; i++) {
    totals.set(start.clone().add(i, "days").format("YYYY-MM-DD"), 0);
  }

  for (const transaction of within(transactions, start, end)) {
    const key = toDayjs(transaction.transactionDate)!.format("YYYY-MM-DD");
    if (totals.has(key)) {
      totals.set(key, totals.get(key)! + magnitude(transaction));
    }
  }

  return Array.from(totals.entries()).map(([date, total]) => ({ date, total }));
}

/**
 * Trailing `days` against the `days` immediately before it.
 *
 * Note this is a rolling window, not a calendar month. The two are different
 * numbers and the label has to match whichever one is shown.
 */
export function toPeriodComparison(
  transactions: SpendTransaction[],
  days: number,
  now: Dayjs = dayjs(),
): PeriodComparison {
  const end = now.clone().startOf("day").add(1, "day");
  const currentStart = end.clone().subtract(days, "days");
  const previousStart = currentStart.clone().subtract(days, "days");

  const current = sum(within(transactions, currentStart, end));
  const previous = sum(within(transactions, previousStart, currentStart));

  return {
    current,
    previous,
    changePercent: previous
      ? Math.round(((current - previous) / previous) * 100)
      : null,
  };
}

/**
 * `weeks` weekly buckets for the trailing block, each paired with the matching
 * week of the block before it, oldest first.
 *
 * Weeks are counted back from today rather than snapped to calendar weeks, so
 * "week 4" always ends today and the two series line up period-over-period.
 */
export function toWeeklyComparison(
  transactions: SpendTransaction[],
  weeks: number,
  now: Dayjs = dayjs(),
): WeeklyBucket[] {
  const end = now.clone().startOf("day").add(1, "day");

  return Array.from({ length: weeks }, (_, i) => {
    const weeksBack = weeks - i;
    const currentStart = end.clone().subtract(weeksBack, "weeks");
    const currentEnd = currentStart.clone().add(1, "week");
    const previousStart = currentStart.clone().subtract(weeks, "weeks");
    const previousEnd = previousStart.clone().add(1, "week");

    return {
      week: i + 1,
      current: sum(within(transactions, currentStart, currentEnd)),
      previous: sum(within(transactions, previousStart, previousEnd)),
    };
  });
}

/**
 * Spend per linked account across the trailing `days`, keyed by `accountId`.
 * Feeds the amount on each Connected accounts row.
 */
export function toAccountTotals(
  transactions: SpendTransaction[],
  days: number,
  now: Dayjs = dayjs(),
): Record<string, number> {
  const end = now.clone().startOf("day").add(1, "day");
  const start = end.clone().subtract(days, "days");

  const totals: Record<string, number> = {};
  for (const transaction of within(transactions, start, end)) {
    const key = transaction.accountId;
    if (!key) continue;
    totals[key] = (totals[key] ?? 0) + magnitude(transaction);
  }
  return totals;
}
